import * as Sentry from '@sentry/vue'
import stringHash from '@sindresorhus/string-hash'
import {
	SDK,
	RetryError,
	TimeoutError,
	type Context,
	type EventLogger,
	type EventLoggerData,
	type Exposure,
} from '@simple-life-medical/ab-platform-sdk-typescript'

import analytics from '@/modules/analytics'
import { ENV, ENV_DEV_OR_STAGING } from '@/helpers/environment'
import { useMainStore } from '@/store/main'
import { useFeatureFlagsStore } from '@/store/featureFlags'
import { useLocationStore } from '@/store/location'
import { useStatusStore } from '@/store/status'

// AB-platform (in-house ABSmartly replacement) client — DEV-35179.
//
// Same shape as src/modules/analytics.ts: a singleton module, and no other module
// imports the SDK directly.  Design decisions (docs/ab-platform.md):
//  - unit = the funnel's sessionId (platform unit type `sessionId`), so the SDK must be
//    initialised only after adoptCrmSessionFromUrl() may have replaced it;
//  - the only context attribute is the derived `session_bucket` (0..9) — audience filters
//    and ramp-up run on it; nothing user-entered ever goes to the AB platform (PHI);
//  - exposures reach our own analytics (`ExperimentExposure`) through the SDK event
//    logger; nothing is published to the platform backend (serverEventsDisabled);
//  - first paint waits for the context through the status-store render gate
//    ('abPlatform'), so variant-dependent content can't flash the control version —
//    but the gate is bounded: it opens on ready, on failure and on a hard deadline,
//    and every failure degrades to variant 0 with no exposure (fail-open; unlike
//    simple-performance-web, a dead AB backend can only delay the splash, never hang it).

const endpoint: string = import.meta.env.VITE_AB_PLATFORM_URL ?? ''
const apiKey: string = import.meta.env.VITE_AB_PLATFORM_KEY ?? ''

// `?safe` is the escape hatch (QA links, incident debugging): the SDK is not even
// constructed — treatment() stays 0 and no exposure is emitted.  Same convention and
// same read path as simple-performance-web: the location store's getQueryParam checks
// the entry URL first and falls back to the persisted queryParams (7-day `vuex` blob,
// hydrated by App.vue's storeQueryParams), so `safe` survives in-app navigation AND
// page reloads — a QA session can't silently slip back into the experiment.
// transformQueryParam turns a bare `?safe` into boolean true, hence Boolean().
// Note the flip side of the persistence: the way OUT of safe mode is clearing site
// data (or waiting out the 7-day TTL).
function isSafeMode(): boolean {
	return Boolean(useLocationStore().getQueryParam('safe'))
}

// `?exp=<name>~<variant>` forces a variant (repeatable param — getQueryParam special-
// cases `exp` as multi-value).  Reload-persistent through the same queryParams fallback
// as `safe`; the persisted form is a bare string for a single occurrence, an array for
// several.  Overridden assignments are flagged by the SDK (`assigned: false,
// overridden: true`), so they never produce an ExperimentExposure event — QA traffic
// stays out of the experiment analysis.
function parseExpOverrides(): Record<string, number> {
	const overrides: Record<string, number> = {}
	const value = useLocationStore().getQueryParam('exp')
	const entries = Array.isArray(value) ? value : [value]
	for (const raw of entries) {
		if (typeof raw !== 'string') continue
		const [name, variant] = raw.split('~')
		// Non-negative only: the SDK throws on negative variants, and this runs at boot.
		if (name && variant && Number.isInteger(Number(variant)) && Number(variant) >= 0) {
			overrides[name] = Number(variant)
		}
	}
	return overrides
}

// Cap on how long the render gate may hold first paint waiting for the AB context.
// The SDK's per-attempt timeout is 3 s, so a hanging backend hits this deadline right
// after its first attempt; an ad-blocked / refused request fails (and opens the gate)
// in milliseconds. Runs concurrently with the other splash-blocking fetches (geo, i18n,
// feature flags — the latter has its own 4 s deadline), so it extends the worst-case
// splash only when everything else was faster.
const READY_GATE_DEADLINE_MS = 3000

let context: Context | null = null
let contextReady = false

function openRenderGate(): void {
	useStatusStore().setResourceReadiness('abPlatform')
}

// The CMS feature-flag is the fleet-wide kill-switch: checked at treatment/exposure
// time, not at init — flags hydrate in the router guard, which always resolves before
// the first screen (and therefore before the first treatment() call) paints.
function experimentsEnabled(): boolean {
	return !isSafeMode() && useFeatureFlagsStore().getAbPlatformEnabled
}

let reportedToSentry = false

function handleError(error: unknown): void {
	// Timeouts / exhausted retries are expected funnel noise (flaky mobile networks,
	// ad-blockers) and the module fails open — don't turn them into GlitchTip issues.
	if (error instanceof TimeoutError || error instanceof RetryError) {
		if (ENV_DEV_OR_STAGING) {
			console.warn('ab-platform [debug]: context fetch failed', error)
		}
		return
	}
	// One event per page load, silenced entirely by the kill-switch: a broken AB backend
	// answering 4xx would otherwise report on every single funnel visit.  Synthetic
	// message on purpose — the SDK uses the raw response body as Error.message, which can
	// vary per request and would shatter GlitchTip grouping; the original goes to context
	// (same scrub-and-attach convention as src/modules/sentry/network.ts).
	if (reportedToSentry || !experimentsEnabled()) return
	reportedToSentry = true
	Sentry.captureException(new Error('AB-platform SDK error'), {
		tags: { module: 'abPlatform' },
		contexts: { ab_platform: { original: String(error).slice(0, 500) } },
	})
}

function handleExposure(exposure: Exposure): void {
	// Only genuine assignments feed the experiment analysis: overridden (?exp=) carry
	// assigned=false, audience mismatches are non-participants by definition.
	if (!exposure.assigned || !exposure.eligible || exposure.audienceMismatch) return
	if (!experimentsEnabled()) return

	// Exposure itself has no iteration — resolve it from the experiment data, like
	// simple-performance-web does.
	const iteration = context?.data()?.experiments?.find((experiment) => experiment.name === exposure.name)?.iteration

	// The SDK swallows event-logger exceptions (bare catch) — without this guard a
	// schema regression (event/field renamed in a vendored-analytics bump) would drop
	// every exposure with zero signal while the funnel looks healthy.
	try {
		// Formats per the analysts' schema spec (and the simple-performance-web
		// convention): iteration is `i1 | i2 | …`, variant is the test-group letter.
		analytics.track('ExperimentExposure', {
			name: exposure.name,
			...(iteration !== undefined && { iteration: `i${iteration}` }),
			variant: variantLetter(exposure.variant),
		})
	} catch (error) {
		handleError(error)
	}
}

// Test-group letter (A | B | C | …) from the numeric variant index — the format the
// analysts' schema expects and simple-performance-web uses.
function variantLetter(variant: number): string {
	return String.fromCharCode(65 + variant)
}

// Dev/staging console logger, same shape as the med-analytics one (analytics.ts): a
// collapsed group with a grey prefix, the data object visible only when expanded.
// Exposure — THE record QA looks for — is the only colored entry (fuchsia, absent from
// the med-analytics palette) and carries the experiment + group letter in the header.
function logDebugEvent(eventName: string, data?: EventLoggerData): void {
	/* eslint-disable no-console */
	if (eventName === 'exposure' && data && 'exposedAt' in data) {
		console.groupCollapsed(
			`%cab-platform [debug]: %cexposure – ${data.name} – ${variantLetter(data.variant)}`,
			'font-weight: 400; color: #888',
			'color: #d946ef; font-weight: 700',
		)
	} else {
		console.groupCollapsed(`%cab-platform [debug]: %c${eventName}`, 'font-weight: 400; color: #888', '')
	}
	if (data !== undefined) console.log(data)
	console.groupEnd()
	/* eslint-enable no-console */
}

const eventLogger: EventLogger = (_context, eventName, data) => {
	if (eventName === 'error') handleError(data)
	// `in` narrows the EventLoggerData union to Exposure — only it has `exposedAt`.
	if (eventName === 'exposure' && data && 'exposedAt' in data) handleExposure(data)
	if (ENV_DEV_OR_STAGING && eventName !== 'error') logDebugEvent(eventName, data)
}

/**
 * Constructs the SDK and kicks off the context fetch (GET /v1/context).  Non-blocking:
 * nothing awaits it at boot — call sites that need a variant go through treatment() /
 * treatmentWhenReady() and fail open to 0.
 *
 * Must run after app.use(pinia) AND after adoptCrmSessionFromUrl(): the unit and the
 * derived session_bucket both have to ride the CRM-adopted sessionId, or the same user
 * would be re-randomized between the mailing click and the original visit.
 */
export function initAbPlatform(): void {
	if (context) return
	if (isSafeMode() || !endpoint || !apiKey) {
		openRenderGate()
		return
	}

	// This runs at boot, before app.mount() — a synchronous throw here (SDK input
	// validation: negative ?exp variant, malformed persisted sessionId) must degrade to
	// "no experiments", never to a blank page.
	try {
		// serverEventsDisabled is the med fork's flag: exposures/goals are never queued or
		// published to the platform backend (it ignores them anyway) — no PUT /context, no
		// api key in browser traffic. The eventLogger 'exposure' callback still fires.
		const sdk = new SDK({
			endpoint,
			apiKey,
			environment: ENV === 'production' ? 'production' : 'development',
			application: 'web',
			eventLogger,
			serverEventsDisabled: true,
		})

		const sessionId = useMainStore().sessionId
		context = sdk.createContext({ units: { sessionId } })

		// Attributes are buffered by the SDK until the context data arrives; the audience
		// expression (session_bucket range) is evaluated lazily at treatment() time, so
		// setting them here — before ready — is the ordering the audience filter relies on.
		context.attributes({ session_bucket: stringHash(sessionId) % 10 })
		context.overrides(parseExpOverrides())

		const ready = context
			.ready()
			.then(() => {
				contextReady = true
			})
			.catch(() => {
				/* already reported via the event logger's 'error' event */
			})

		// Bounded render gate: whichever settles first — context resolution (success or
		// failure) or the deadline — opens first paint. After a deadline-open, a late
		// context still flips contextReady, but the step-0 treatment() has already
		// returned control by then (lost exposure, symmetric for both variants).
		void Promise.race([ready, new Promise((resolve) => setTimeout(resolve, READY_GATE_DEADLINE_MS))]).then(
			openRenderGate,
		)
	} catch (error) {
		context = null
		handleError(error)
		openRenderGate()
	}
}

/**
 * Variant index for the experiment (0 = control/unassigned).  The first call for a
 * genuinely assigned unit records the exposure (→ ExperimentExposure analytics event).
 *
 * Synchronous by design: first paint is gated on the 'abPlatform' status resource, so
 * by the time any component renders and calls this, the context is either ready or has
 * failed / hit the gate deadline — in which case this returns 0 with no exposure.
 * Never throws: not initialised / not ready / kill-switched / unknown experiment → 0.
 */
export function treatment(experimentName: string): number {
	if (!context || !contextReady || !experimentsEnabled()) return 0
	try {
		return context.treatment(experimentName)
	} catch (error) {
		handleError(error)
		return 0
	}
}
